/**
 * What moves on the map, and where it goes.
 *
 * Every animated thing is a `MapEvent`: a particle of some `kind` leaving one node and landing on another.
 * Events come from three feeds, and this module turns each into events with ONE set of rules:
 *
 *  - the AgentAction ledger (`/api/agent-actions` for the replay, the AGENT_ACTION duplexer channel live).
 *    Its `label` is FREE-FORM (the tool's own examples are 'notification' | 'nudge' | 'optimization' |
 *    'research' | 'outreach'), so the destination is decided by keyword, with a catch-all. The `action`
 *    sentence is searched too, because "Wrote the scope doc" under label 'research' is still a file.
 *  - AGENT_TASK changes: a task that just started runs on its agent → the agent pulses.
 *  - FILE_SYSTEM_CHANGED: a file was written and we do not know by whom → a file from the Maestro to Files.
 *
 * Pure. Mirrored on iOS (`MapEvents.swift`) and Android (`MapEvents.kt`); the keyword tables are shared and
 * the parity tests feed the same rows through all three.
 */
import {
  BRIEF_ID,
  MAESTRO_ID,
  agentNodeId,
  bucketNodeId,
  channelInNodeId,
  connectorNodeId,
  outreachNodeId,
  spaceNodeId,
  type BucketId,
} from './map-layout';

export type MapEventKind = 'file' | 'outreach' | 'message' | 'research' | 'task' | 'goal' | 'routine' | 'notification' | 'app' | 'brief' | 'generic';

export interface MapEvent {
  id: string;
  /** When it happened, epoch ms. Replay orders on it; live events are "now". */
  at: number;
  kind: MapEventKind;
  from: string;
  to: string;
  /** The sentence to show on the info card — the ledger's `action`, or a generated line. */
  text: string;
  /** The agent it belongs to, when known — drives the particle's colour. */
  agentId?: string;
  relatedTaskId?: string;
  /** Where the full thing lives (a task, a person's correspondence, the notifications) — the card's link. */
  href?: string;
  /** The Space the work belongs to, when known — the district its particle lands in. */
  workspaceId?: string;
}

/** The ledger row, as the API and the duplexer both carry it (dates as ISO strings). */
export interface ActionRow {
  id: string;
  createdDate?: string;
  agentId?: string;
  label?: string;
  action?: string;
  relatedTaskId?: string;
  /** The Space the action's task belongs to, when the row says. */
  workspaceId?: string;
}

/** Ordered: the first table that matches wins. Outreach before message, so "sent a WhatsApp outreach" leaves
 *  to the right rather than arriving from the left. */
const KIND_KEYWORDS: [MapEventKind, RegExp][] = [
  [
    'outreach',
    /\b(outreach|reached out|follow[- ]?up(?:s|ed)?|sent (?:a |an |the )?(?:message|email|whatsapp|sms|text|reply|invite|reminder)|emailed|messaged|texted|replied)\b/i,
  ],
  [
    'file',
    /\b(file|doc(?:ument)?s?|report|dashboard|page|spreadsheet|sheet|csv|pdf|deck|slides?|wrote|written|drafted|saved|exported|created (?:a |an |the )?(?:file|doc|report|dashboard|page|list))\b/i,
  ],
  ['research', /\b(research(?:ed)?|browsed?|searched|looked up|scraped|fetched|analy[sz]ed|benchmark(?:ed)?|compared|checked|reviewed)\b/i],
  ['notification', /\b(notif(?:ication|ied)|nudge[ds]?|alert(?:ed)?|reminded|pinged)\b/i],
  ['task', /\b(task|to-?do|scheduled|booked|rescheduled|cancelled|canceled)\b/i],
  ['goal', /\b(goal|milestone|objective)\b/i],
  ['routine', /\b(routine|pulse|recurring|cron)\b/i],
  ['app', /\b(app|widget|mini-?app|built|deployed|published)\b/i],
  ['message', /\b(message|whatsapp|email|sms|slack|inbox|received|replied to)\b/i],
];

/** Where each kind lands, by default. Outreach and message need a channel; see `channelFor`. */
const KIND_BUCKET: Partial<Record<MapEventKind, BucketId>> = {
  file: 'files',
  task: 'tasks',
  goal: 'goals',
  routine: 'routines',
  app: 'apps',
  notification: 'messages',
};

/** Channel slugs we know how to name from a sentence — the ones the connectors screen carries. */
const CHANNEL_KEYWORDS: [string, RegExp][] = [
  ['whatsapp', /\bwhats ?app\b/i],
  ['gmail', /\bgmail\b/i],
  ['outlook', /\boutlook\b/i],
  ['email', /\be-?mail(?:ed|s)?\b/i],
  ['slack', /\bslack\b/i],
  ['imessage', /\bi-?message\b/i],
  ['sms', /\b(sms|text(?:ed| message)?)\b/i],
  ['phone', /\b(phone|call(?:ed)?|voice)\b/i],
  ['instagram', /\binstagram\b/i],
  ['messenger', /\b(messenger|facebook)\b/i],
  ['x', /\b(x dm|twitter)\b/i],
  ['linkedin', /\blinkedin\b/i],
  ['telegram', /\btelegram\b/i],
  ['googlecalendar', /\bcalendar\b/i],
];

/** A named channel → the OUTREACH node it leaves on (gmail/outlook are the email channel). */
const OUTREACH_FOR_CHANNEL: Record<string, string> = { gmail: 'email', outlook: 'email' };

export function classify(label: string | undefined, action: string | undefined): MapEventKind {
  const haystack = `${label ?? ''} ${action ?? ''}`;
  for (const [kind, re] of KIND_KEYWORDS) if (re.test(haystack)) return kind;
  return 'generic';
}

export function channelFor(text: string): string | null {
  for (const [slug, re] of CHANNEL_KEYWORDS) if (re.test(text)) return slug;
  return null;
}

/** The first drawn connector whose slug (or its words — `google_search_console` → "search console") the
 *  sentence names. Slugs are the connectors screen's; the words are what a model writes. */
export function connectorFor(text: string, drawnConnectorSlugs: string[]): string | null {
  const hay = text.toLowerCase();
  // Whole words only, and the always-present Wix last: "wixel" or "wix site" in a sentence about Notion
  // must not land every research event on the Wix tile that is first in every list.
  const ordered = [...drawnConnectorSlugs.filter((s) => s !== 'wix'), ...drawnConnectorSlugs.filter((s) => s === 'wix')];
  for (const slug of ordered) {
    const words = slug
      .toLowerCase()
      .replace(/^calendar-/, '')
      .replace(/[_-]+/g, ' ')
      .trim();
    if (!words) continue;
    const esc = (w: string) => w.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    if (new RegExp(`\\b${esc(words)}\\b`).test(hay) || new RegExp(`\\b${esc(words.replace(/\s+/g, ''))}\\b`).test(hay)) return slug;
  }
  return null;
}

/**
 * Resolve the destination node id for a kind. `known` says which channel / connector nodes the world
 * actually drew, so an outreach on WhatsApp lands on the WhatsApp node when it exists and on the first
 * outreach node otherwise; a research event pulses a connector when one is named, else the Maestro.
 */
export interface KnownNodes {
  outreach: string[];
  channelsIn: string[];
  connectors: string[];
  /** The Space ids that have a district on the map. */
  spaces?: string[];
}

/** Where work lands: its Space's district when the map draws one, else the kind's bucket (off the bottom
 *  edge, toward the tile) — work with no Space has no plate to land on. */
export function landingFor(bucket: BucketId, workspaceId: string | undefined, known: KnownNodes): string {
  return workspaceId && known.spaces?.includes(workspaceId) ? spaceNodeId(workspaceId) : bucketNodeId(bucket);
}

export function destinationFor(kind: MapEventKind, text: string, known: KnownNodes, workspaceId?: string): string {
  const bucket = KIND_BUCKET[kind];
  if (bucket) return landingFor(bucket, workspaceId, known);
  const channel = channelFor(text);
  if (kind === 'outreach') {
    const out = channel ? OUTREACH_FOR_CHANNEL[channel] ?? channel : null;
    if (out && known.outreach.includes(out)) return outreachNodeId(out);
    return known.outreach[0] ? outreachNodeId(known.outreach[0]) : bucketNodeId('messages');
  }
  if (kind === 'message') {
    if (channel && known.channelsIn.includes(channel)) return channelInNodeId(channel);
    return known.channelsIn[0] ? channelInNodeId(known.channelsIn[0]) : bucketNodeId('messages');
  }
  if (kind === 'research') {
    const tool = connectorFor(text, known.connectors) ?? (channel && known.connectors.includes(channel) ? channel : null);
    return tool ? connectorNodeId(tool) : MAESTRO_ID;
  }
  if (kind === 'brief') return BRIEF_ID;
  return MAESTRO_ID;
}

/** The node an action leaves from: its agent when the world drew that agent, else the Maestro. */
export function originFor(agentId: string | undefined, drawnAgentIds: string[]): string {
  return agentId && drawnAgentIds.includes(agentId) ? agentNodeId(agentId) : MAESTRO_ID;
}

export function parseIso(iso: string | undefined): number | null {
  if (!iso) return null;
  const ms = Date.parse(iso);
  return Number.isNaN(ms) ? null : ms;
}

/**
 * A ledger row → one event. An inbound message is drawn ARRIVING (channel → agent), everything else
 * LEAVING (agent → destination). A row with no parseable date is placed at `fallbackAt`.
 */
export function eventFromAction(row: ActionRow, known: KnownNodes, drawnAgentIds: string[], fallbackAt: number): MapEvent {
  const kind = classify(row.label, row.action);
  const text = `${row.label ?? ''} ${row.action ?? ''}`;
  const origin = originFor(row.agentId, drawnAgentIds);
  const dest = destinationFor(kind, text, known, row.workspaceId);
  const arriving = kind === 'message';
  return {
    id: `action:${row.id}`,
    at: parseIso(row.createdDate) ?? fallbackAt,
    kind,
    from: arriving ? dest : origin,
    to: arriving ? origin : dest,
    text: (row.action ?? row.label ?? '').trim(),
    ...(row.agentId ? { agentId: row.agentId } : {}),
    ...(row.relatedTaskId ? { relatedTaskId: row.relatedTaskId } : {}),
    ...(row.workspaceId ? { workspaceId: row.workspaceId } : {}),
  };
}

/** A task that just started (or is running) on an agent: the Maestro hands it to that agent. */
export function eventFromTaskStart(task: { id: string; agentId?: string; title?: string }, drawnAgentIds: string[], at: number): MapEvent {
  const to = originFor(task.agentId, drawnAgentIds);
  return {
    id: `task-start:${task.id}:${at}`,
    at,
    kind: 'task',
    from: MAESTRO_ID,
    to: to === MAESTRO_ID ? bucketNodeId('tasks') : to,
    text: task.title ?? '',
    ...(task.agentId ? { agentId: task.agentId } : {}),
    relatedTaskId: task.id,
  };
}

/** A file changed and nobody said who wrote it: a file from the Maestro into Files. */
export function eventFromFileChange(entityId: string, at: number, known: KnownNodes, workspaceId?: string): MapEvent {
  return {
    id: `fs:${entityId}:${at}`,
    at,
    kind: 'file',
    from: MAESTRO_ID,
    to: landingFor('files', workspaceId, known),
    text: '',
    ...(workspaceId ? { workspaceId } : {}),
  };
}

/**
 * Which tile a landed event bumps, if any — so the tile's count can tick the moment the particle arrives
 * rather than when the next poll answers.
 */
export function bucketOf(event: MapEvent): BucketId | null {
  // By KIND — the destination is a Space district now, so the tile that bumps is the kind's — but only
  // for a particle that LANDED on the ground (a district or a bucket): a task handed to an agent, a
  // message into the Maestro, an outreach onto a channel bump nothing.
  if (!event.to.startsWith('space:') && !event.to.startsWith('bucket:')) return null;
  return KIND_BUCKET[event.kind] ?? null;
}
